import { useCallback, useState, useEffect } from "react";
import { useBreakpointValue } from "@chakra-ui/react";
import circle from "@turf/circle";
import { lineString } from "@turf/helpers";
import Div100vh from "react-div-100vh";
import { MotionBox as Box, viewAppearDefault } from "utils/motion";
import mapboxgl from "mapbox-gl/dist/mapbox-gl-csp";
import Map, {
  Source,
  Layer,
  Marker,
  NavigationControl,
  GeolocateControl,
} from "react-map-gl";
import MapPanelView from "components/blocks/MapPanelView";
import MoveArrow from "components/icons/MoveArrow";
import HeliIcon from "components/icons/HeliIcon";
import {
  getMaxDistance,
  minGrossWeight,
  minPassengersWeight,
} from "utils/mapUtils";
import "mapbox-gl/dist/mapbox-gl.css";

mapboxgl.workerUrl = "/mapbox-gl-csp-worker.js";

// Pass the CSP-split mapboxgl instance directly to react-map-gl so it
// uses our pre-configured library (with workerUrl set) instead of its
// own dynamic import of mapbox-gl which would pull in the broken UMD bundle.
const mapLib = Promise.resolve(mapboxgl);

const MAPBOX_TOKEN = process.env.NEXT_PUBLIC_MAPBOX_TOKEN;
const colors = {
  primary: "#00E66E",
  secondary: "#188f51",
  bg: "rgba(5, 252, 123, 0.12)",
};

const MapView = ({
  viewState,
  onMove,
  onGeolocate,
  marker,
  onMarkerDragStart,
  onMarkerDrag,
  onMarkerDragEnd,
  dragged,
  iconScale,
  dataCircle,
  dataLine,
  mobile,
}) => (
  <Box
    h="100vh"
    display={mobile ? ["block", "block", "none"] : ["none", "none", "block"]}
    variants={viewAppearDefault}
    initial="hidden"
    whileInView="visible"
  >
    <Map
      {...viewState}
      onMove={onMove}
      mapStyle="mapbox://styles/mapbox/dark-v9"
      mapboxAccessToken={MAPBOX_TOKEN}
      projection="globe"
      attributionControl={false}
      mapLib={mapLib}
    >
      <GeolocateControl
        position={mobile ? "top-right" : "bottom-left"}
        fitBoundsOptions={{ maxZoom: mobile ? 2.7 : 3.5 }}
        onGeolocate={onGeolocate}
        trackUserLocation
        showUserLocation={false}
        style={{ marginTop: mobile ? "90px" : "0px" }}
      />
      <NavigationControl
        position={mobile ? "top-right" : "bottom-left"}
        showCompass={false}
      />
      <Source id="my-data" type="geojson" data={dataCircle}>
        <Layer id="circle" type="fill" paint={{ "fill-color": colors.bg }} />
      </Source>
      <Source id="my-data-line" type="geojson" data={dataLine}>
        <Layer
          id="circle-line"
          type="line"
          paint={{ "line-color": colors.primary, "line-width": 2 }}
        />
      </Source>
      <Marker
        longitude={marker.lng}
        latitude={marker.lat}
        anchor="center"
        draggable
        onDragStart={onMarkerDragStart}
        onDrag={onMarkerDrag}
        onDragEnd={onMarkerDragEnd}
      >
        <Box
          p="20px"
          _hover={{ cursor: "move" }}
          style={{ transform: `scale(${iconScale})` }}
        >
          {dragged ? <HeliIcon /> : <MoveArrow />}
        </Box>
      </Marker>
    </Map>
  </Box>
);

const MapViewContainer = () => {
  const defaultViewState = useBreakpointValue(
    {
      base: { longitude: 2, latitude: 53, zoom: 2.7 },
      lg: { longitude: 13, latitude: 53, zoom: 3.5 },
    },
    { fallback: "lg", ssr: true },
  );
  const [viewState, setViewState] = useState(defaultViewState);
  const [marker, setMarker] = useState({ lat: 51.5072, lng: 0.1276 });
  const [dragging, setDragging] = useState(false);
  const [dragged, setDragged] = useState(false);
  const [passengers, setPassengers] = useState(minPassengersWeight);
  const [cargo, setCargo] = useState(0);

  useEffect(() => {
    if (dragging) {
      setDragged(true);
    }
  }, [dragging]);

  const options = { steps: 50, units: "kilometers" };
  // converting from NM to km
  const radius = getMaxDistance(minGrossWeight + passengers + cargo) * 1.852;
  const dataCircle = circle([marker.lng, marker.lat], radius, options);

  const dataLine = lineString(...dataCircle.geometry.coordinates);

  const onMarkerDragStart = useCallback(() => setDragging(true), []);
  const onMarkerDrag = useCallback((event) => setMarker(event.lngLat), []);
  const onMarkerDragEnd = useCallback(() => setDragging(false), []);
  const iconScale =
    viewState && viewState.zoom
      ? viewState.zoom > 2.5
        ? 1
        : viewState.zoom - viewState.zoom * 0.6
      : 1;

  return (
    <Div100vh>
      <Box h="100%" position="relative" style={{ overflow: "hidden" }}>
        <MapView
          viewState={viewState}
          onMove={(evt) => setViewState(evt.viewState)}
          marker={marker}
          onGeolocate={({ coords }) =>
            setMarker({ lat: coords.latitude, lng: coords.longitude })
          }
          dragged={dragged}
          onMarkerDragStart={onMarkerDragStart}
          onMarkerDrag={onMarkerDrag}
          onMarkerDragEnd={onMarkerDragEnd}
          iconScale={iconScale}
          dataLine={dataLine}
          dataCircle={dataCircle}
        />
        <MapView
          mobile
          viewState={viewState}
          onMove={(evt) => setViewState(evt.viewState)}
          marker={marker}
          onGeolocate={({ coords }) =>
            setMarker({ lat: coords.latitude, lng: coords.longitude })
          }
          dragged={dragged}
          onMarkerDragStart={onMarkerDragStart}
          onMarkerDrag={onMarkerDrag}
          onMarkerDragEnd={onMarkerDragEnd}
          iconScale={iconScale}
          dataLine={dataLine}
          dataCircle={dataCircle}
        />
        <MapPanelView
          passengers={passengers}
          onPassengersChange={(value) => setPassengers(value)}
          cargo={cargo}
          onCargoChange={(value) => setCargo(value)}
        />
      </Box>
    </Div100vh>
  );
};

export default MapViewContainer;
